import { useMemo, useState } from "react"
import { formatPercent } from "atlas_forecasting_app/lib/format"
import { clamp01 } from "atlas_forecasting_app/lib/math"
import { useContainerWidth } from "atlas_forecasting_app/hooks/use_container_width"
import { ChartTooltip } from "atlas_forecasting_app/components/charts/chart_tooltip"

export function MultipleChoiceBarChart({ entries, height, valueLabel = "Probability" }) {
  const [ref, width] = useContainerWidth()
  const [hoverIdx, setHoverIdx] = useState(null)
  const [tooltip, setTooltip] = useState(null)
  const gradId = useMemo(() => `af_bar_${Math.random().toString(16).slice(2)}`, [])

  const rowHeight = 28
  const barHeight = 12
  const leftMargin = width > 0 && width < 520 ? 140 : 190
  const margin = { top: 18, right: 58, bottom: 30, left: leftMargin }

  const computedHeight = height || Math.max(160, margin.top + margin.bottom + entries.length * rowHeight)
  const innerW = Math.max(10, (width || 0) - margin.left - margin.right)
  const innerH = Math.max(10, computedHeight - margin.top - margin.bottom)

  const onMove = (event) => {
    if (!width) return
    const rect = event.currentTarget.getBoundingClientRect()
    const x = event.clientX - rect.left
    const y = event.clientY - rect.top

      const idx = Math.floor((y - margin.top) / rowHeight)
      if (idx >= 0 && idx < entries.length) {
        setHoverIdx(idx)
        const [label, value] = entries[idx]
        setTooltip({ x, y, title: label, body: `${valueLabel}: ${formatPercent(value, 2)}` })
      } else {
        setHoverIdx(null)
        setTooltip(null)
      }
  }

  return (
    <div ref={ref} className="af-chart" style={{ height: `${computedHeight}px` }}>
      {width > 0 ? (
        <svg
          width={width}
          height={computedHeight}
          onMouseMove={onMove}
          onMouseLeave={() => {
            setHoverIdx(null)
            setTooltip(null)
          }}
        >
          <defs>
            <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="rgba(34, 139, 230, 0.92)" />
              <stop offset="100%" stopColor="rgba(18, 184, 134, 0.80)" />
            </linearGradient>
          </defs>

          {[0, 0.25, 0.5, 0.75, 1].map((t) => {
            const x = margin.left + innerW * t
            return (
              <g key={t}>
                <line x1={x} y1={margin.top - 2} x2={x} y2={margin.top + innerH + 6} className="af-chart-grid-soft" />
                <text x={x} y={computedHeight - 10} textAnchor="middle" className="af-chart-axis-muted">
                  {(t * 100).toFixed(0)}%
                </text>
              </g>
            )
          })}

          {entries.map(([label, value], idx) => {
            const yCenter = margin.top + idx * rowHeight + rowHeight / 2
            const barY = yCenter - barHeight / 2
            const isHover = idx === hoverIdx

            const clamped = clamp01(Number(value)) ?? 0
            const w = innerW * clamped
            const maxLabelChars = margin.left < 170 ? 18 : 24

            return (
              <g key={label}>
                {isHover ? <rect x={8} y={yCenter - rowHeight / 2} width={width - 16} height={rowHeight} rx="10" fill="rgba(34, 139, 230, 0.06)" /> : null}

                <text x={margin.left - 10} y={yCenter + 4} textAnchor="end" className="af-chart-axis">
                  {label.length > maxLabelChars ? `${label.slice(0, maxLabelChars)}…` : label}
                </text>

                <rect x={margin.left} y={barY} width={innerW} height={barHeight} rx="7" className="af-chart-bar-muted" />
                <rect x={margin.left} y={barY} width={w} height={barHeight} rx="7" fill={`url(#${gradId})`} />

                <text x={margin.left + innerW + 8} y={yCenter + 4} textAnchor="start" className="af-chart-axis-muted">
                  {formatPercent(clamped, 1)}
                </text>
              </g>
            )
          })}
        </svg>
      ) : null}
      <ChartTooltip tooltip={tooltip} containerWidth={width} containerHeight={computedHeight} />
    </div>
  )
}
